import React from "react";
import { TextInput, Text } from "design_system";
import useLabels from "hooks/useLabels";
import { Role } from "features/auth/types/onboarding";
import {
  StudentEmailError,
  StudentEmailErrorType,
} from "features/auth/types/studentEmailErrors";
import { STUDENT_EMAIL } from "constants/config";

interface StudentEmailSectionProps {
  studentEmail: string;
  onEmailChange: (email: string) => void;
  emailErrorType: StudentEmailErrorType | null;
  isDisabled?: boolean;
  selectedRole?: Role | null;
}

export function StudentEmailSection({
  studentEmail,
  onEmailChange,
  emailErrorType,
  isDisabled = false,
  selectedRole,
}: StudentEmailSectionProps) {
  const [authLabels] = useLabels("auth");

  // Determine which label to use based on the selected role
  const getPromoLabel = () => {
    if (selectedRole === "undergrad") {
      return authLabels.onboarding.labels["student-email-promo-undergrad"];
    } else if (selectedRole === "graduate-student") {
      return authLabels.onboarding.labels["student-email-promo-graduate"];
    }
    // Fallback to default label
    return authLabels.onboarding.labels["student-email-promo"];
  };

  // Render error based on error type
  const renderError = () => {
    if (!emailErrorType) return null;

    // Special rendering for not-student-email error with email link
    if (emailErrorType === StudentEmailError.NOT_STUDENT_EMAIL) {
      return (
        <Text size="sm" theme="muted">
          {authLabels.errors["not-student-email-prefix"]}
          <a href={`mailto:${STUDENT_EMAIL}`} className="underline">
            {STUDENT_EMAIL}
          </a>
          {authLabels.errors["not-student-email-suffix"]}
        </Text>
      );
    }

    // For other errors (invalid-email), just display the error text
    return (
      <Text size="sm" className="text-chart-yellow-base!">
        {authLabels.errors[emailErrorType]}
      </Text>
    );
  };

  return (
    <div className="flex flex-col gap-2">
      <Text size="sm" weight="medium" className="text-fg-base">
        {getPromoLabel()}
      </Text>
      <div className="flex flex-col gap-1">
        <TextInput
          id="student-email-input"
          value={studentEmail}
          onChange={(e) => onEmailChange(e.target.value)}
          placeholder={authLabels.onboarding.placeholders["add-edu-email"]}
          type="email"
          isDisabled={isDisabled}
          hasError={!!emailErrorType}
        />
        {renderError()}
      </div>
    </div>
  );
}
